import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { RevealImage, AssetPlaceholder } from "@/components/RevealImage";
import Testimonial from "@/components/Testimonial";

/* ────────────────────────────────────────────────────────────
   BECOME — Shiva Murti Sacred Healing Arts
   Single accent on this page: EMERALD.
   Text-led. Generous breathing room. No payment flow.
   ──────────────────────────────────────────────────────────── */

const WEEKS = [
  {
    title: "Lineage, Bali & the sacred foundation",
    body: "Where the practice comes from; the gratitude mantra; preparing your space.",
  },
  {
    title: "Dewata Nawa Sanga, the elements & the sacred mantra",
    body: "The wheel, the directions, becoming a clearer vessel.",
  },
  {
    title: "Animistic principles, the four siblings & the guides",
    body: "The unseen relationships that support the work.",
  },
  {
    title: "Kundalini, Shakti fire & protection",
    body: "Building a strong energetic field around the practitioner.",
  },
  {
    title: "Channeling energy, self-healing & expansion",
    body: "Strengthening the vessel; holding and transmitting energy responsibly.",
  },
  {
    title: "Testing points & in-person healing",
    body: "Reading the body, finding blocks, supervised full-body sessions.",
  },
  {
    title: "Online Healing Social and Graduation",
    body: "Taking all the training and sharing it through Zoom — the Shiva Murti tradition that completes the path.",
  },
];

const WHO = [
  "Sincere students who feel called to healing work and sacred service.",
  "Sensitive or intuitive people who want structure, protection, and grounded practice.",
  "Coaches, healers, bodyworkers, yoga teachers, meditation guides, caregivers, and space holders who want to go deeper in their work.",
  "Those who wish to support themselves, family, children, pets, home, and community — with humility and care.",
  "Students willing to honour the lineage, practise consistently, and respect the responsibility of this work.",
];

const Become = () => {
  // Curriculum download — email capture then open PDF
  const [curriculumEmail, setCurriculumEmail] = useState("");
  const [curriculumSubmitting, setCurriculumSubmitting] = useState(false);
  const [curriculumSent, setCurriculumSent] = useState(false);

  const onSubmitCurriculum = async (e) => {
    e.preventDefault();
    const email = curriculumEmail.trim();
    if (!email) {
      toast("Please leave your email so we can send the packet.");
      return;
    }
    setCurriculumSubmitting(true);
    // Open the PDF window synchronously so mobile Safari doesn't block it as
    // an untrusted popup. We'll set its location after the request completes.
    const pdfWindow = window.open("", "_blank", "noopener,noreferrer");
    try {
      const { data } = await api.post("/curriculum-requests", {
        email,
        source: "become_curriculum_download",
      });
      const url = data?.pdf_url || "/assets/shiva-murti-level-1-curriculum.pdf";
      if (pdfWindow) pdfWindow.location = url;
      setCurriculumSent(true);
    } catch (err) {
      if (pdfWindow) pdfWindow.close();
      toast("Something interrupted the request. Please try again.");
    } finally {
      setCurriculumSubmitting(false);
    }
  };

  return (
    <>
      <SEO path="/become" />

      {/* ── 1. HERO — anchored on Dewata Nawa Sanga, restrained ─────── */}
      <section className="bg-ivory" data-testid="become-hero">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pt-24 md:pt-40 pb-20 md:pb-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="md:col-span-7 md:order-1 order-2">
              <p className="eyebrow" data-testid="hero-eyebrow">
                Become · The path of Shiva Murti
              </p>
              <h1
                className="mt-5 font-serif font-light text-temple leading-[0.95] tracking-[0.005em]"
                style={{ fontSize: "clamp(2.6rem, 6vw, 5.5rem)" }}
                data-testid="hero-headline"
              >
                Become clear,<br />
                protected, of service.
              </h1>
              <p className="mt-6 font-sans font-light text-temple text-base md:text-lg leading-[1.7] max-w-prose">
                A seven-week initiation into the practice of Shiva Murti — carried
                from the teachings of Ratu Nabe. This is not about
                becoming powerful.
              </p>
            </div>
            <div className="md:col-span-5 md:order-2 order-1">
              <RevealImage
                src="/assets/become/hero-practitioner.png"
                alt="Practitioner and client within a sumi-e enso brushstroke ring"
                className="w-full md:w-[115%] md:-ml-[7.5%] aspect-[5/6] md:aspect-[4/5]"
                imgClassName="object-cover object-center ink-bleed scale-110 md:scale-125"
                loading="eager"
                fetchpriority="high"
                testId="hero-dewata"
              />
            </div>
          </div>
          <div className="mt-14 md:mt-20" data-testid="hero-cta-row">
            <a
              href="https://api.leadconnectorhq.com/widget/form/DqN6Yeb54wuZGddqp6bG"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-emerald"
              data-testid="hero-apply-cta"
            >
              Apply now
              <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── 2. THE LINEAGE — Temple Black, type-as-art centrepiece ────── */}
      <section className="bg-temple text-ivory grain" data-testid="become-lineage">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="eyebrow">The lineage</p>
          <div className="mt-6 font-sans font-light text-ivory/85 text-base md:text-lg leading-[1.9] space-y-6 max-w-prose">
            <p>
              Shiva Murti is rooted in Balinese tradition — and in the older
              currents that fed it: the rishis and sages of ancient India, the
              animistic and shamanic ways of Bali, and the teachings carried
              through Ratu Nabe.
            </p>
          </div>

          {/* TYPE AS ART — designed centrepiece, not floating paragraph */}
          <div className="mt-16 md:mt-20 text-center" data-testid="lineage-statement">
            <hr className="hairline mx-auto w-16" />
            <p
              className="mt-10 md:mt-14 font-serif font-light text-ivory leading-[1.05] tracking-[0.005em]"
              style={{ fontSize: "clamp(1.9rem, 4vw, 3.4rem)" }}
            >
              You do not invent the work.{" "}
              <span className="block mt-3 italic text-gold">You receive it, and you carry it.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ── 3. WHO THIS IS FOR — Warm Sand, image LEFT, list RIGHT (zigzag) ── */}
      <section className="bg-sand grain" data-testid="become-who">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-12 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-5" data-testid="who-image">
              <RevealImage
                src="/assets/become/dewata-devotion.png"
                alt="Priest in prayer beneath the gold Dewata Nawa Sanga wheel"
                className="w-full max-w-[440px] mx-auto aspect-square"
                imgClassName="object-contain ink-bleed"
                testId="who-image-placeholder"
              />
            </div>
            <div className="md:col-span-7">
              <p className="eyebrow eyebrow--stone">Who this is for</p>
              <h2 className="mt-5 font-serif font-light text-temple leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.25rem)" }}>
                A path entered<br/>in earnest.
              </h2>
              <ol className="mt-10 list-none" data-testid="who-list">
                {WHO.map((line, i) => (
                  <li
                    key={line}
                    className="border-t border-temple/30 py-6 grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline"
                    data-testid={`who-${i + 1}`}
                  >
                    <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-serif font-light text-temple text-lg md:text-xl leading-[1.3]">
                      {line}
                    </p>
                  </li>
                ))}
                <li className="border-t border-b border-temple/30" />
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. WHAT LEVEL 1 - FOUNDATIONS IS — seven-week sequence ─────── */}
      <section className="bg-ivory border-t border-temple/10" data-testid="become-foundations">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-28 md:py-40">
          <div className="max-w-3xl mx-auto">
            <p className="eyebrow text-center">What Level 1 - Foundations is</p>
            <h2 className="mt-8 font-serif font-light text-temple text-4xl md:text-6xl leading-[1.05] text-center">
              A seven-week initiation.
            </h2>
            <p className="mt-10 font-sans font-light text-temple text-base md:text-lg leading-[1.8] max-w-prose mx-auto text-center">
              Each week opens one part of the foundation.
            </p>
          </div>

          <ol className="mt-12 max-w-3xl mx-auto border-t border-gold/60" data-testid="weeks-list">
            {WEEKS.map((w, i) => (
              <li
                key={w.title}
                className="border-b border-temple/15 py-8 md:py-10 grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline"
                data-testid={`week-${i + 1}`}
              >
                <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-gold">
                  Week&nbsp;{String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif font-light text-temple text-2xl md:text-3xl leading-[1.2]">
                    {w.title}
                  </h3>
                  <p className="mt-3 font-sans font-light text-stone text-sm md:text-base leading-[1.8] max-w-prose">
                    {w.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <p className="mt-16 max-w-3xl mx-auto font-sans font-light text-stone text-sm md:text-base leading-[1.8] text-center">
            The full information packet — the complete detail of each class —
            is given to those who apply. Some of the practice is only shared
            inside the training, where it can be carried with the right context
            and protection.
          </p>

          <div className="mt-16 max-w-3xl mx-auto" data-testid="foundations-set-apart">
            <hr className="hairline" />
          </div>
        </div>
      </section>

      {/* ── Texture break — mandala centrepiece (Temple Black band) ─────── */}
      <section className="bg-temple grain" data-testid="become-texture">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-14 md:py-20 flex justify-center">
          <img
            src="/assets/shiva-murti-wheel.png"
            alt=""
            className="block h-32 md:h-44 w-auto select-none"
            loading="lazy"
            decoding="async"
            data-testid="become-texture-mandala"
          />
        </div>
      </section>

      {/* ── 5. THE CERTIFICATION PATHWAY — REMOVED per request ───────── */}

      {/* ── 7. SACRED BOUNDARY & ETHICS — dignified, not fine print ─── */}
      <section className="bg-ivory border-t border-temple/10" data-testid="become-ethics">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-28 md:py-40">
          <p className="eyebrow">Sacred boundary &amp; ethics</p>
          <h2 className="mt-8 font-serif font-light text-temple text-4xl md:text-6xl leading-[1.1]">
            Honoured with dignity.
          </h2>
          <div className="mt-10 font-sans font-light text-temple text-base md:text-lg leading-[1.9] space-y-6 max-w-prose">
            <p>
              This training honours teachings traditionally passed from teacher
              to student. Some practices, mantras, and methods are shared only
              inside the training container, where students receive the proper
              context, protection, and supervision.
            </p>
            <p>
              Shiva Murti is a spiritual and energetic healing practice. It is
              not a replacement for medical care, therapy, diagnosis, emergency
              support, or professional mental health treatment. Students are
              taught to practise with consent, humility, energetic hygiene, and
              clear boundaries. Results vary, and no specific outcome is
              guaranteed.
            </p>
          </div>
        </div>
      </section>

      {/* ── 8. HOW TO ENTER — free Immersion, then Level 1 ─── */}
      <section className="bg-ivory border-t border-temple/10" data-testid="become-entries">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-28 md:py-40">
          <p className="eyebrow text-center">How to enter</p>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-0 max-w-5xl mx-auto">
            <article className="md:pr-14 md:border-r md:border-gold/50" data-testid="entry-immersion">
              <p className="eyebrow eyebrow--stone">First step · Complimentary</p>
              <h3 className="mt-6 font-serif font-light text-temple text-3xl md:text-5xl leading-[1.1]">
                The 5-Day<br />
                <span className="text-stone italic">Immersion.</span>
              </h3>
              <p className="mt-8 font-sans font-light text-temple text-base md:text-lg leading-[1.9] max-w-prose">
                Five short self-paced lessons, one each day. Each pairs a
                teaching with a live practice: a healing activation, the Nine
                Guardians, a chakra meditation and a water blessing. Receive the
                energy yourself, and discern whether this path is yours.
              </p>
              <p className="mt-6 font-sans font-light text-stone text-sm leading-[1.8]">
                20–30 minutes a day · No experience needed · Taught by Johnny Can
              </p>
              <div className="mt-10">
                <a
                  href="https://jpppaklpbcxj9gk8ruh4.app.clientclub.net/courses/offers/7fe67c06-5356-42f5-a34a-fbd0794a995d"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                  data-testid="immersion-cta"
                >
                  Begin the free Immersion
                  <span aria-hidden>→</span>
                </a>
              </div>
            </article>
            <article className="md:pl-14" data-testid="entry-level-1">
              <p className="eyebrow eyebrow--stone">Certification · By application</p>
              <h3 className="mt-6 font-serif font-light text-temple text-3xl md:text-5xl leading-[1.1]">
                Apply for<br />
                <span className="text-stone italic">Level 1 - Foundations.</span>
              </h3>
              <p className="mt-8 font-sans font-light text-temple text-base md:text-lg leading-[1.9] max-w-prose">
                The seven-week cohort certification. Seating is limited to
                preserve the quality of the container. Entry is by application
                and expression of interest.
              </p>
              <div className="mt-10">
                <a
                  href="https://api.leadconnectorhq.com/widget/form/DqN6Yeb54wuZGddqp6bG"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-emerald"
                  data-testid="level1-cta"
                >
                  Apply · Express interest
                  <span aria-hidden>→</span>
                </a>
              </div>
            </article>
          </div>

          {/* Curriculum PDF — email-gated download. Captures serious readers
              to MongoDB while still delivering the packet in-flow. */}
          <div
            className="mt-16 md:mt-20 pt-10 border-t border-temple/15"
            data-testid="become-curriculum"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
              <div className="md:col-span-5">
                <p className="font-sans font-light text-stone text-sm md:text-base leading-[1.7]">
                  For those who would rather read before speaking —
                  the full Level&nbsp;1 Foundations information packet.
                </p>
              </div>

              <div className="md:col-span-7">
                {curriculumSent ? (
                  <p
                    className="font-sans font-light text-temple text-base leading-[1.7]"
                    data-testid="curriculum-sent"
                  >
                    Sent — the packet is opening in a new tab. If your browser
                    blocked it,{" "}
                    <a
                      href="/assets/shiva-murti-level-1-curriculum.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-gold underline-offset-4"
                      data-testid="curriculum-manual-link"
                    >
                      download it here
                    </a>
                    .
                  </p>
                ) : (
                  <form
                    onSubmit={onSubmitCurriculum}
                    className="flex flex-col sm:flex-row items-stretch gap-3"
                    data-testid="curriculum-form"
                    noValidate
                  >
                    <label htmlFor="curriculum-email" className="sr-only">
                      Your email
                    </label>
                    <input
                      id="curriculum-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="Your email"
                      value={curriculumEmail}
                      onChange={(e) => setCurriculumEmail(e.target.value)}
                      className="field-box flex-1 min-w-0"
                      data-testid="curriculum-email-input"
                      disabled={curriculumSubmitting}
                    />
                    <button
                      type="submit"
                      className="btn-ghost whitespace-nowrap self-stretch sm:self-auto"
                      disabled={curriculumSubmitting}
                      data-testid="curriculum-submit"
                    >
                      {curriculumSubmitting ? "Sending…" : "Send me the curriculum"}
                      <span aria-hidden>↓</span>
                    </button>
                  </form>
                )}
                <p className="mt-3 font-sans font-light text-stone text-xs leading-[1.7]">
                  Your email is used only to send this packet and, if you write
                  to us later, to reply.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Naga Temple — full-bleed connector band ─────────────────── */}
      <section className="bg-ivory" data-testid="become-naga-band">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-10 md:py-16">
          <RevealImage
            src="/assets/become/naga-temple.png"
            alt="Balinese temple gateway guarded by golden naga dragons"
            className="w-full max-w-3xl mx-auto aspect-[16/10]"
            imgClassName="object-contain ink-bleed"
            testId="naga-band-placeholder"
          />
        </div>
      </section>

      {/* ── 9. INVESTMENT — REMOVED per request ─────────────────────── */}

      {/* ── 9b. TESTIMONIAL — graduate's word, near the application CTA ── */}
      <Testimonial
        bg="ivory"
        testId="become-testimonial"
        quote="My curiosity to personal development and understanding myself was the driving point to taking this class. My experience upon completion and commitment to its daily practice has improved my life in many ways. Given me great foundations to build upon and recommended to anyone seeking. Thank you to Jero Jason and Johnny for the introduction to Siwa Murti."
        name="Phu T."
        descriptor="Level 1 graduate"
      />

      {/* ── 9c. TESTIMONIAL — longer-form narrative (Kevin Lee) ────────── */}
      <section
        className="bg-sand border-t border-temple/10"
        data-testid="become-testimonial-long"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <hr className="hairline mx-auto w-16" />
          <blockquote
            className="mt-12 md:mt-16 font-sans font-light text-temple text-base md:text-lg leading-[1.85] space-y-6"
            data-testid="testimonial-long-quote"
          >
            <p className="relative">
              <span
                aria-hidden
                className="absolute -left-6 md:-left-8 -top-1 font-serif text-gold text-3xl md:text-4xl leading-none"
              >
                “
              </span>
              I came into Johnny&apos;s Shiva Murti Level One course after an
              eight-year spiritual dry spell — spirituality had once been a
              real foundation for me, and somewhere in the busyness of
              entrepreneurship, I&apos;d lost that spark.
            </p>
            <p>
              Coming in, I was struggling to even understand how to access and
              channel kundalini energy in preparation for the healing work.
              In about five minutes, working with Johnny in person, he helped
              me find and channel it — something real effort on my own hadn&apos;t
              gotten me to. That moment alone was worth the course.
            </p>
            <p className="relative pr-6">
              Johnny is generous, patient, and deeply skilled, and the course
              itself is well-organized with a genuinely supportive community
              around it. But what I&apos;ll remember most is that he helped me
              reconnect with a part of myself I thought I&apos;d lost. I&apos;m
              looking forward to going deeper into my Shiva Murti practice
              from here.
              <span
                aria-hidden
                className="ml-1 font-serif text-gold text-3xl md:text-4xl leading-none align-bottom"
              >
                ”
              </span>
            </p>
          </blockquote>
          <p
            className="mt-10 font-sans font-light text-stone text-[0.72rem] tracking-[0.28em] uppercase"
            data-testid="testimonial-long-attribution"
          >
            Kevin Lee · Level 1 graduate
          </p>
        </div>
      </section>

      {/* ── 10. NEXT STEP — application / expression of interest ─────── */}
      <section
        id="express-interest"
        className="bg-ivory border-t border-temple/10"
        data-testid="become-apply"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <p className="eyebrow text-center">Express interest</p>
          <h2 className="mt-8 font-serif font-light text-temple text-3xl md:text-5xl leading-[1.1] max-w-2xl mx-auto">
            If you feel called,<br />
            <span className="text-stone italic">write to us.</span>
          </h2>
          <p className="mt-8 font-sans font-light text-temple text-base md:text-lg leading-[1.8] max-w-prose mx-auto">
            Cohort dates, deposit details, and next steps follow personally
            after we hear from you.
          </p>
          <div className="mt-12" data-testid="apply-cta-row">
            <Link
              to="/contact"
              className="btn-emerald"
              data-testid="apply-write-cta"
            >
              Write to us
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── 11. CLOSING BEAT ─────────────────────────────────────────── */}
      <section className="bg-ivory" data-testid="become-closing">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-16 text-center">
          <hr className="hairline" />
          <p className="mt-12 font-serif font-light text-temple text-4xl md:text-6xl leading-[1.2] tracking-[0.04em]">
            Clear. Protect. Heal. Serve.
          </p>
        </div>
      </section>
    </>
  );
};

export default Become;
